import { TimeAgo } from '@/components/shared/timeAgo/TimeAgo';
import useTenantState from '@/states/tenant.state';
import useNotificationState from '@/states/notification.state';
import { INotification, NotificationType } from '@/types/notification';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, CheckCircle, UserPlus, Bell } from 'lucide-react';
import { useEffect } from 'react';

interface NotificationContentProps {
  onClose?: () => void;
}

// Get icon based on notification type
const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'App\\Notifications\\DiscussionMessage':
      return <MessageSquare size={16} className="text-blue-500" />;
    case 'App\\Notifications\\ACKConfirmation':
      return <CheckCircle size={16} className="text-green-500" />;
    case 'App\\Notifications\\PICAssignment':
      return <UserPlus size={16} className="text-purple-500" />;
    default:
      return <Bell size={16} className="text-gray-500" />;
  }
};

export const NotificationContent = ({ onClose }: NotificationContentProps) => {
  const { currentTenant } = useTenantState();
  const { notifications, unreadCount, isLoading, fetchNotifications, markAsRead, markAllAsRead } =
    useNotificationState();
  const navigate = useNavigate();

  // Fetch notifications when component mounts
  useEffect(() => {
    if (currentTenant?.id) {
      fetchNotifications(currentTenant.id);
    }
  }, [currentTenant?.id, fetchNotifications]);

  const handleNotificationClick = async (notification: INotification) => {
    // Mark as read if not already
    if (!notification.is_read && currentTenant?.id) {
      await markAsRead(currentTenant.id, notification.id);
    }

    // Navigate to action URL
    if (notification.data.action_url) {
      navigate(`/${currentTenant?.slug}${notification.data.action_url}`);
      onClose?.();
    }
  };

  const handleMarkAllAsRead = async () => {
    if (currentTenant?.id) {
      await markAllAsRead(currentTenant.id);
    }
  };

  return (
    <div className="p-3 bg-white rounded-xl shadow-xl w-[calc(100vw-2rem)] md:w-96 fixed left-1/2 -translate-x-1/2 top-14 md:absolute md:top-full md:right-0 md:left-auto md:translate-x-0 md:mt-2">
      {/* Header */}
      <div className="flex items-center justify-between mx-3 mb-3">
        <h3 className="font-semibold text-gray-800">Notifikasi</h3>
        {unreadCount > 0 && (
          <button
            className="text-xs text-primary hover:text-primary/80 transition-colors"
            onClick={handleMarkAllAsRead}
          >
            Tandai Semua Sudah Dibaca
          </button>
        )}
      </div>

      {/* Notification List */}
      <div className="flex-col mx-3 mb-3 max-h-96 overflow-auto">
        {isLoading ? (
          <div className="py-6">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          </div>
        ) : notifications && notifications.length > 0 ? (
          notifications.slice(0, 10).map((notification: INotification) => (
            <button
              key={notification.id}
              className="text-left w-full"
              onClick={() => handleNotificationClick(notification)}
            >
              <div
                className={`relative w-full p-3 mb-2 border rounded-md transition-colors hover:bg-gray-50 ${
                  notification.is_read ? 'border-gray-200 bg-white' : 'border-blue-200 bg-blue-50/30'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5">{getNotificationIcon(notification.type)}</div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{notification.data.title}</p>
                    <p className="mt-1 text-xs text-gray-600 line-clamp-2">{notification.data.message}</p>
                    <div className="mt-1">
                      <TimeAgo timestamp={notification.created_at} />
                    </div>
                  </div>

                  {/* Unread indicator */}
                  {!notification.is_read && (
                    <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1"></span>
                  )}
                </div>
              </div>
            </button>
          ))
        ) : (
          <div className="py-6">
            <div className="flex flex-col items-center justify-center text-gray-400">
              <Bell size={32} className="mb-2 opacity-50" />
              <p className="text-sm">Tidak ada notifikasi</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center pt-2 border-t border-gray-100">
        <Link
          to={`/${currentTenant?.slug}/notifications`}
          className="text-sm text-primary hover:text-primary/80 transition-colors"
          onClick={() => onClose?.()}
        >
          Lihat semua notifikasi
        </Link>
      </div>
    </div>
  );
};
